<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrIcon from "../global/WrIcon.vue";

interface ServiceCard {
  svgIcon?: string;
  icon?: string;
  title: string;
  desc: string;
}

const emit = defineEmits<{ "cta-clicked": [] }>();

const props = withDefaults(
  defineProps<{
    headingLine1: string;
    headingLine2: string;
    subcopy: string;
    cards: ServiceCard[];
    bgImageDark?: string;
    bgImageLight?: string;
    ctaText?: string;
    ctaLink?: string | null;
    descClass?: string;
  }>(),
  {
    bgImageDark: "",
    bgImageLight: "",
    ctaText: "Explore More",
    ctaLink: null,
    descClass: "",
  },
);
</script>

<template>
  <section
    class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px] relative"
    aria-label="Service cards"
  >
    <div class="relative z-[1]">
      <!-- Heading -->
      <h2
        class="text-[28px] lg:text-[48px] font-semibold leading-tight text-center font-avant-demi text-transparent bg-[linear-gradient(92deg,#12DBFF_-3.57%,#BA55FE_80.72%)] bg-clip-text"
      >
        {{ props.headingLine1 }}<br class="lg:hidden" />
        {{ props.headingLine2 }}
      </h2>
      <p
        class="text-[16px] lg:text-[18px] font-normal tracking-[0.36px] mt-4 text-center px-4 font-avant text-black opacity-70 dark:text-white"
      >
        {{ subcopy }}
      </p>

      <!-- Service Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 px-4 max-w-[1100px] mx-auto">
        <div
          v-for="(service, sIndex) in props.cards"
          :key="service.title"
          class="relative flex flex-col items-start bg-transparent border-l pl-7 pb-7 border-[rgba(225,184,255,1)] dark:border-[rgba(50,39,61,1)]"
        >
          <!-- Icon -->
          <div class="size-6 sm:size-8 mt-6 mb-3 sm:mb-0 flex-shrink-0">
            <WrIcon
              :name="service.svgIcon || 'streamline:interface-setting-cog-work-loading-cog-gear-settings-machine'"
              class="size-6 sm:size-8 fill-current text-black dark:text-white"
            />
          </div>
          <h3
            class="relative self-stretch text-left break-words font-avant text-[20px] font-normal leading-normal before:h-[17px] before:w-[1px] before:[content=''] before:bg-[rgba(193,155,222,1)]
            before:dark:bg-[rgba(109,45,158,1)]
            before:absolute before:-left-[29px] before:top-2 text-black dark:text-white"
          >
            {{ service.title }}
          </h3>
          <p
            class="text-left font-avant text-[15px] font-normal leading-normal tracking-[0.3px]"
            :class="descClass || 'text-[#501D71] dark:text-[#AF66E4]'"
          >
            {{ service.desc }}
          </p>
        </div>
      </div>
    </div>

    <!-- CTA Button -->
    <div class="flex justify-center mt-6">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[40px] lg:h-[48px] lg:mt-5 mt-3"
        append-class="text-[16px] lg:font-normal px-3 lg:px-5"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>
